/**
 * Temporarily switch things off for a clean capture (share photo, photo mode, video frames).
 *
 * Scene markers are selected by ENTITY, not by layer: hotspot and portal markers share
 * HotspotMeshLayer (and TranslucentEntityLayer, once a marker drops below full opacity) with
 * every custom GLB model, so switching those layers off would also remove the creator's own
 * 3D content. The walk-mode reticle has a layer of its own and is still hidden that way.
 * Anything that was already disabled stays disabled after restore.
 */
export interface CaptureHideable {
    enabled: boolean;
}

export function hideForCapture(targets: CaptureHideable[]): () => void {
    const previous = targets.map((target) => target.enabled);
    for (const target of targets) target.enabled = false;
    let restored = false;
    return () => {
        if (restored) return;
        restored = true;
        targets.forEach((target, index) => {
            // A marker rebuilt or destroyed while hidden (e.g. a leader line edited
            // mid-capture) leaves a dead component behind; restoring it must not throw.
            try { target.enabled = previous[index]; } catch { /* target is gone */ }
        });
    };
}

/** The part of a PlayCanvas entity the marker selection reads (pc.Entity satisfies it). */
export interface CaptureMarkerNode {
    /** The render component, switched off rather than the entity: the viewer drives
     *  marker `entity.enabled` every frame (visibility ranges, tap prompts). */
    render?: CaptureHideable | null;
    children: readonly CaptureMarkerNode[];
}

/** A hotspot or portal root entity as the viewer keeps it. */
export interface CaptureMarkerEntity extends CaptureMarkerNode {
    /** Leader line drawn from the marker; a sibling in the scene graph, not a child. */
    leaderLineEntity?: CaptureMarkerNode | null;
    /** Set only on hotspots that render a video plane: a video hotspot, or a stacked marker with a video layer. */
    videoElement?: object | null;
    /** Layer planes of a stacked marker (`type: 'stack'`); children of the marker entity. */
    markerStackLayers?: readonly CaptureMarkerNode[];
}

function collectSubtreeRenders(
    node: CaptureMarkerNode,
    into: CaptureHideable[],
    skipOwnRender = false,
    keepPlanes?: ReadonlySet<CaptureMarkerNode>,
): void {
    if (!skipOwnRender && node.render) into.push(node.render);
    for (const child of node.children) collectSubtreeRenders(child, into, !!keepPlanes?.has(child), keepPlanes);
}

/**
 * Render components to switch off so a capture shows no scene markers: every hotspot and
 * portal marker (with its children, e.g. a portal's popup label) and every leader line.
 *
 * Kept visible: custom 3D models (never in these lists, whatever layer they share) and the
 * plane of a video hotspot, which is content the creator placed in the scene. Only its
 * "Tap to Start" prompt (a child) and its leader line go. A stacked hotspot with a video
 * layer is the same kind of content (a video sign with its labels), so every layer plane
 * stays and only its prompt and leader line go; an image / GIF-only stack is a marker and is
 * hidden like an image hotspot. Portals are navigation, so every portal marker is hidden.
 */
export function collectMarkerRenders(markers: {
    hotspots: readonly CaptureMarkerEntity[];
    portals: readonly CaptureMarkerEntity[];
}): CaptureHideable[] {
    const renders: CaptureHideable[] = [];
    for (const hotspot of markers.hotspots) {
        const isVideoContent = !!hotspot.videoElement;
        const keepPlanes = isVideoContent && hotspot.markerStackLayers?.length
            ? new Set(hotspot.markerStackLayers)
            : undefined;
        collectSubtreeRenders(hotspot, renders, isVideoContent, keepPlanes);
        if (hotspot.leaderLineEntity) collectSubtreeRenders(hotspot.leaderLineEntity, renders);
    }
    for (const portal of markers.portals) {
        collectSubtreeRenders(portal, renders);
        if (portal.leaderLineEntity) collectSubtreeRenders(portal.leaderLineEntity, renders);
    }
    return renders;
}
